import { useState } from 'react'

import { getErrorMessage } from 'utils'
import { useCanAccess } from 'hooks'
import { useReceivePackage } from 'hooks/api'

import { Stack, Tooltip, Typography } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'

/** What a receipt would act on. Mirrors the API's `notReceived` / `receipt` payload. */
export interface ReceivePackagePrompt {
  /** Never null here: a tag with no package cannot be received, so it must not raise a prompt. */
  packageId: string
  packageName: string | null
  /** Tags in the WHOLE package, which is what a receipt moves. 0 when the count is unknown. */
  tagCount: number
}

interface ConfirmReceivePackageModalProps {
  /** The package to receive, or `null` to keep the modal closed. */
  prompt: ReceivePackagePrompt | null
  onClose: () => void
  /** Ran after the receipt lands. The caller decides what "then proceed" means for its flow. */
  onReceived: (prompt: ReceivePackagePrompt) => void | Promise<void>
}

/**
 * VERA-455 — offer the receipt that resolves an `at_vera` scan refusal.
 *
 * THE PROBLEM IT EXISTS FOR. An operator holding a real box, scanning its tags in Create
 * Certificates, was told "Tag has not shipped yet" and given nothing to do about it. The
 * sentence is true in the data and false in the room, and per `working-conventions.md` §46 a
 * block that refuses an action must leave the RESOLVING action reachable. Receipt is that
 * action, and until now it lived only on the Inventory page, three navigations away from the
 * modal the operator is standing in.
 *
 * WHY A PROMPT RATHER THAN INFERRING IT FROM A TAG SCAN. `tag_possession_events` is an audit
 * trail, so an inferred receipt would timestamp the box as arriving "whenever someone got
 * round to certifying it" rather than when it actually landed, and would attribute it to
 * whoever happened to be minting. So: prompt, one click, then proceed.
 *
 * Note what that argument does and does not cover, since a PACKAGE-label scan now receives
 * itself (below). It is an argument about EVIDENCE, not about clicks: a tag scan is evidence
 * that one tag is in the room, and the receipt asserts the box is. A package-label scan is
 * evidence about the box directly, so there is nothing left to infer, and the event it writes
 * is still a real receipt through the same audited endpoint — attributed to the operator who
 * scanned the label, timestamped when they scanned it. The `at_vera` guard stays reachable
 * either way; it is what raises both paths in the first place.
 *
 * THE COUNT IS NOT DECORATION. `TagPossessionService.transitionPackageTags` moves every tag
 * whose `package_id` matches, so a receipt raised from ONE tag scan marks the entire box
 * available — up to ~100 siblings. A prompt that said only "Receive?" would be asking consent
 * for something an order of magnitude larger than what the operator did, so the scope is the
 * first thing the body states.
 *
 * ## WHO STILL SEES THIS (VERA-455, revised)
 *
 * The SINGLE-TAG path, which is the one where the leap exists. A scanned PACKAGE LABEL no
 * longer prompts at all: `ScanSessionBlock.handleAddPackage` receives the box and carries on,
 * because "receive this package?" asked of someone who just scanned that package's own label is
 * a confirmation with no decision in it. It still reaches this modal on that path as a
 * FALLBACK — no `tags_inventory_receive` permission, or an automatic receipt that failed —
 * where the operator does need a surface that can say why.
 *
 * ## WHY NOT `ConfirmSettingModal`, WHICH THIS WAS BUILT ON
 *
 * That component renders its `consequence` inside a warning `Alert`, unconditionally and on
 * purpose (`working-conventions` §43): it gates SETTINGS whose harm accrues in a window you
 * cannot recover — scans taken with location off have no location, forever. Receiving a box you
 * are physically holding is not that. It is a routine operational act, done dozens of times a
 * shift, and it is reversible in the sense that matters: the receipt is an audit row, not a
 * destroyed record. Warning chrome on it overstates the stakes, and spending the warning
 * treatment on the routine case is what makes it stop working on the case that needs it.
 *
 * So this is the plain shared `Modal` (§28/§47 — still the shared component, just the right one)
 * with descriptive body copy. The one thing that stays an `Alert` is a FAILED receipt, which is
 * a genuine error and reads as one.
 */
export const ConfirmReceivePackageModal = ({
  prompt,
  onClose,
  onReceived,
}: ConfirmReceivePackageModalProps) => {
  const [error, setError] = useState<string | null>(null)
  const receivePackage = useReceivePackage()

  // §27/§46 — receipt is an IN-CONTEXT action on a surface the user can already see, so a
  // user without the permission gets the confirm DISABLED with a tooltip naming it, never a
  // hidden button. There IS an admin who can grant `tags_inventory_receive` (it is a company
  // permission, not an internal-only one), so "ask an admin" is an honest path rather than a
  // false promise.
  const { isHasAccess: canReceive } = useCanAccess({ action: 'tags_inventory_receive' })

  const handleConfirm = async () => {
    if (!prompt || receivePackage.isPending) return
    setError(null)
    try {
      await receivePackage.mutateAsync({ packageId: prompt.packageId })
      await onReceived(prompt)
      onClose()
    } catch (err) {
      // Kept OPEN on failure. Closing would drop the operator back into a scan surface that
      // still refuses the tag, with the reason gone.
      setError(getErrorMessage(err))
    }
  }

  const handleClose = () => {
    if (receivePackage.isPending) return
    setError(null)
    onClose()
  }

  const boxName = prompt?.packageName?.trim() || 'this package'
  // A count of 0 means "not readable", not "an empty box" — the API degrades to 0 when the
  // aggregate fails. Saying "(0 tags)" would be a claim we cannot stand behind, so the scope
  // is stated without a number instead.
  const hasCount = Boolean(prompt && prompt.tagCount > 0)
  // Only ever read inside the `hasCount` branch — the countless case says "every tag" instead,
  // rather than a phrase with a number we cannot vouch for.
  const tagsPhrase = prompt ? `${prompt.tagCount} tag${prompt.tagCount === 1 ? '' : 's'}` : ''

  const confirmTooltip = 'You need the “Receive inventory” permission. Ask an admin to grant it.'

  return (
    <Modal
      open={Boolean(prompt)}
      onClose={handleClose}
      /**
       * A QUESTION about a state change, not an order to act. "Receive this package (2 tags)?"
       * put the scope in the title, where it read as the modal's subject — as though 2 tags were
       * the thing being received — and "receive" alone leaves the operator to work out what the
       * word does to their inventory. "Mark ... received" names the state it sets, and the
       * count moves into the body where it can be explained instead of parenthesised.
       */
      title="Mark this package received?"
      actions={
        <Stack direction="row" spacing={3} alignItems="center">
          <Button
            variant="secondary"
            size="medium"
            onClick={handleClose}
            disabled={receivePackage.isPending}
          >
            Cancel
          </Button>
          {/* §46 — a disabled control must carry a tooltip that says why. A disabled button
              emits no hover events, so the span is the hover target (the same wrapper
              `PermissionGate` uses); rendered only when disabled so the enabled path keeps
              its plain markup. */}
          {canReceive ? (
            <Button
              variant="primary"
              size="medium"
              onClick={handleConfirm}
              loading={receivePackage.isPending}
            >
              Receive package
            </Button>
          ) : (
            <Tooltip title={confirmTooltip} arrow>
              <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                <Button variant="primary" size="medium" disabled>
                  Receive package
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      }
    >
      {/* PLAIN BODY COPY, not a warning `Alert`. See the component note: this is a routine
          operational act, and warning chrome on the routine case is what stops the warning
          treatment working where it is earned. §15 — periods rather than em-dashes, and
          `lineHeight: 1.5` so short copy has room to breathe. */}
      <Stack spacing={3}>
        <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
          {/* THE SCOPE, first sentence, because it is the part that differs from what the
              operator did: they scanned one tag, this moves the whole box.
              "On hand", not "available inventory": receiving is a POSSESSION move, and on the
              Inventory tab that is exactly what the column and the card are called, so the
              operator can go and see the number this changed. */}
          Receiving marks {hasCount ? `all ${tagsPhrase}` : 'every tag'} in {boxName} as on hand
          in your inventory, not just the tag you scanned. They all become available to
          certify.
        </Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          Only do this if the box is physically with you. The receipt is recorded against your
          account, timestamped now.
        </Typography>
        {error && <Alert severity="error" description={error} />}
      </Stack>
    </Modal>
  )
}
